import React from 'react';
import styled, { useTheme } from 'styled-components/macro';
import { useTranslate } from '@federico.moretti/react-translate';
import { Card, Button } from '../../atoms';
import DetailsList from '../DetailsList/DetailsList';
import { IconImage, IconEye } from '../../icons';
import { SPACINGS, formatDate } from '../../utils';
import FileButton from '../FileButton';

const ActionWrapper = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  padding: ${SPACINGS[6]};
`;

type UserInfoProps = {
  title: string;
  name: string;
  surname: string;
  gender: string;
  birthDate: string;
  placeOfBirth: string;
  citizenship: string;
  type: string;
  hide?: string[];
  crmOwnerId?: string;
  crmOwners?: { id: string; label: string }[];
  photo?: { name: string; action: () => void };
  thumbnail?: { name: string; action: () => void };
  action?: React.ReactNode;
  style?: React.CSSProperties;
};
function UserInfo(props: UserInfoProps) {
  const {
    title,
    name,
    surname,
    gender,
    birthDate,
    placeOfBirth,
    citizenship,
    type,
    crmOwnerId,
    crmOwners,
    photo,
    thumbnail,
    hide,
    action,
    style,
  } = props;
  const { t, language } = useTranslate();
  const theme = useTheme();

  const showType = !hide?.includes('type') ?? true;
  const showCrmOwnerId = !hide?.includes('crmOwnerId') ?? true;
  const crmOwner =
    crmOwnerId && crmOwners
      ? crmOwners.find((o) => String(o.id) === String(crmOwnerId))
      : null;

  const items = React.useMemo(
    () => [
      showType && {
        label: t('role'),
        value: t(`userTypes.${type}`),
      },
      showCrmOwnerId && crmOwner
        ? { label: t('crmOwner'), value: crmOwner.label }
        : undefined,
      { label: t('title'), value: title && t(`userTitles.${title}`) },
      { label: t('name'), value: name },
      { label: t('surname'), value: surname },
      { label: t('gender'), value: gender && t(`userGender.${gender}`) },
      {
        label: t('birthDate'),
        value: birthDate && formatDate(birthDate, language),
      },
      { label: t('placeOfBirth'), value: placeOfBirth },
      { label: t('citizenship'), value: citizenship },
      photo && {
        label: t('profileImage'),
        value: <FileButton action={photo.action}>{photo.name}</FileButton>,
      },
      thumbnail && {
        label: t('thumbnail'),
        value: thumbnail && (
          <Button
            styleType="outline"
            type="action"
            onClick={thumbnail.action}
            iconLeft={<IconImage />}
            iconRight={<IconEye stroke={theme.colors.primary} />}
          >
            {thumbnail.name}
          </Button>
        ),
      },
    ],
    [
      showType,
      t,
      type,
      showCrmOwnerId,
      crmOwner,
      title,
      name,
      surname,
      gender,
      birthDate,
      placeOfBirth,
      citizenship,
      language,
      photo,
      theme.colors.primary,
      thumbnail,
    ]
  );

  return (
    <Card style={{ position: 'relative', ...style }}>
      {action && <ActionWrapper>{action}</ActionWrapper>}
      <DetailsList items={items} />
    </Card>
  );
}

export default UserInfo;
